import { Outlet } from 'react-router-dom';
import { useRef } from 'react';
import { Header } from './Header';
import { AppSidebar } from './AppSidebar';
import { SidebarProvider, SidebarTrigger } from './ui/sidebar';
import { useTenants } from '@/hooks/useTenants';
import { MobileNav } from './MobileNav';
import { InstallPrompt } from './pwa/InstallPrompt';
import { OfflineBanner } from './pwa/OfflineBanner';
import { UpdatePrompt } from './pwa/UpdatePrompt';
import { useIsMobile } from '@/hooks/use-mobile';
import { MobilePageTransition } from './mobile/MobilePageTransition';
import { SwipeBackProvider } from './mobile/SwipeBackProvider';
import { PullToRefresh } from './mobile/PullToRefresh';

const LayoutContent = () => {
  // Populate tenant cache globally so all pages can resolve tenant IDs to names
  useTenants();
  const isMobile = useIsMobile();
  const mainRef = useRef<HTMLElement>(null);

  return (
    <div className="min-h-[100svh] bg-brand-background flex w-full">
      <Header className="fixed top-0 left-0 right-0 z-50 mx-0 px-2 sm:px-[11px]">
        <div className="flex items-center gap-2">
          {/* SidebarTrigger drives the desktop sidebar; on phone we use the bottom nav instead. */}
          {!isMobile && <SidebarTrigger />}
        </div>
      </Header>
      {isMobile && <SwipeBackProvider />}
      <div className="flex w-full">
        {!isMobile && <AppSidebar />}
        <main
          ref={mainRef}
          className="flex-1 h-[100svh] md:pt-16 px-0 overflow-x-hidden overflow-y-auto min-w-0 max-w-full transition-all duration-300 ease-out will-change-transform overscroll-y-contain"
          style={{
            paddingTop: isMobile
              ? 'calc(env(safe-area-inset-top) + 3.5rem)'
              : undefined,
            paddingBottom: isMobile
              ? 'calc(env(safe-area-inset-bottom) + 3.5rem)'
              : undefined,
          }}
        >

          {isMobile ? (
            <MobilePageTransition>
              <Outlet />
            </MobilePageTransition>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
      {isMobile && <PullToRefresh scrollTarget={mainRef.current} />}
      <MobileNav />
      <OfflineBanner />
      <UpdatePrompt />
      <InstallPrompt />
    </div>
  );
};

const getSidebarDefault = () => {
  if (typeof document === 'undefined') return true;
  const match = document.cookie.match(/sidebar:state=(\w+)/);
  return match ? match[1] === 'true' : true;
};

const Layout = () => {
  return (
    <SidebarProvider defaultOpen={getSidebarDefault()}>
      <LayoutContent />
    </SidebarProvider>
  );
};
export default Layout;
